<template>
    <div class="d-flex flex-column">
        <Script :src="'https://checkout.flywire.com/flywire-payment.js'" />
        <v-row>
            <v-col md="4" cols="12">
                <date-picker
                    v-model="transactionDateTime"
                    outlined
                    dense
                    :min="minPayDate"
                    :label="$t('invoices.pay_date')"
                    hide-details
                />
            </v-col>
            <v-col v-if="canChangeExchangeRates" md="8" cols="12">
                <v-row v-for="(rate, i) in exchangeRates" :key="i" dense>
                    <v-col md="4" cols="12">
                        <v-text-field
                            v-model="rate.exchangeRate"
                            variant="outlined"
                            :loading="isExchangeRatesLoading"
                            :disabled="isExchangeRatesLoading"
                            density="compact"
                            :items="clientPriceCurrencies"
                            :label="`${$t('exchange_rate')}`"
                        />
                    </v-col>
                    <v-col v-show="clientPriceCurrencies && clientPriceCurrencies.length > 1" md="4" cols="12">
                        <v-select
                            v-model="rate.currencyTo"
                            variant="outlined"
                            :loading="isExchangeRatesLoading"
                            :disabled="isExchangeRatesLoading"
                            density="compact"
                            :items="clientPriceCurrencies"
                            :label="`${$t('currency')}`"
                            @update:model-value="resetExchangeRate(rate)"
                        />
                    </v-col>
                    <v-col md="2" cols="12">
                        <div class="text--secondary mt-3">
                            {{ getNewExchangeAmount(rate) }}
                        </div>
                    </v-col>
                    <v-col v-if="multipleCurrencyChange" md="2" cols="12">
                        <v-btn
                            v-if="i === 0"
                            size="small"
                            color="primary"
                            :disabled="isExchangeRatesLoading"
                            @click="addExchangeRate"
                        >
                            <v-icon> mdi-plus </v-icon>
                        </v-btn>

                        <v-btn
                            v-else
                            size="small"
                            variant="flat"
                            :disabled="isExchangeRatesLoading"
                            @click="removeRate(rate)"
                        >
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-textarea
                    v-model="remark"
                    hide-details
                    rows="3"
                    density="compact"
                    variant="outlined"
                    :label="$t('remark')"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-radio-group v-model="paymentMethodObject" class="mt-0">
                    <template v-for="paymentMethod in paymentMethods" :key="paymentMethod.invoicePaymentKey">
                        <v-radio :value="paymentMethod">
                            <template v-slot:label>
                                <span class="me-2">{{
                                    paymentMethod.paymentMethod || $t('bank_transfer.bank_transfer')
                                }}</span>
                                <span>
                                    {{
                                        $filters.formatPrice({
                                            amount: paymentMethod.paymentAmount,
                                            currency: paymentMethod.currency,
                                        })
                                    }}
                                </span>
                            </template>
                        </v-radio>
                    </template>
                </v-radio-group>
                <div id="dropin-container" />
                <n-m-i-payment-form v-if="nmiPaymentSuccessUrl" @finish-submit="onNMIPaymentFinish" />
            </v-col>
        </v-row>
        <v-expand-transition>
            <bank-transfer-form
                v-if="isShowBankTransferForm"
                v-model="valid"
                class="mb-6"
                :_bank-transfer-details="bankTransferDetails"
                @change-bank-transfer-details="bankTransferDetails = $event"
            />
        </v-expand-transition>
        <v-row>
            <v-col>
                <v-btn color="primary" :disabled="isButtonDisabled" :loading="isPaymentLoading" @click="pay">
                    {{ $t('pay') }}
                </v-btn>
            </v-col>
        </v-row>
        <payment-redirect-form ref="paymentRedirectForm" />
    </div>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, Watch} from 'vue-facing-decorator'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import {EventBus, SHOW_NOTIFICATIONS} from '@/utils/event-bus'
    import PaymentRedirectForm from '@/components/booking/forms/PaymentRedirectForm.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import BankTransferForm from '@/components/account/forms/BankTransferForm.vue'
    import {appInstance} from '@/utils/app-accessor'
    import NMIPaymentForm from '~/components/booking/forms/NMIPaymentForm.vue'

    @Component({
        components: {NMIPaymentForm, BankTransferForm, PaymentRedirectForm, DatePicker},
    })
    export class OrderManualInvoicePaymentForm extends mixins(FormValidationRulesMixin) {
        @Prop() paymentMethods
        @Prop() orderId
        @Prop() clientPriceCurrencies
        @Prop() invoiceType
        @Prop({default: false, type: Boolean}) multipleCurrencyChange
        @Prop({default: false, type: Boolean}) disableMinPayDate

        isPaymentLoading = false

        paymentMethodObject = null
        transactionDateTime = null
        remark = null
        bankTransferDetails = {
            bank: '',
            iban: '',
            branch: '',
            swiftCode: '',
            account: '',
            reference: '',
            receivingBank: '',
            valueDate: '',
        }
        valid = false

        exchangeRatesFromAPI = []
        exchangeRates = []
        isExchangeRatesLoading = false
        nmiPaymentSuccessUrl = null

        @Emit()
        completePayment() {}

        async mounted() {
            this.transactionDateTime = this.$dateFns.format(new Date())

            if (this.canChangeExchangeRates) {
                this.exchangeRates = [
                    {
                        currencyFrom: this.currencyForPayment,
                        currencyTo: this.paymentMethods[0].currency,
                        exchangeRate: null,
                    },
                ]

                try {
                    this.isExchangeRatesLoading = true
                    await this.initExchangeRates()
                    // eslint-disable-next-line no-empty
                } catch (e) {
                } finally {
                    this.isExchangeRatesLoading = false
                }
            }
        }

        async initExchangeRates() {
            this.exchangeRatesFromAPI = await appInstance.$api.exchangeRates.get({
                currencyCode: this.currencyForPayment,
                precision: 10,
            })

            this.resetExchangeRatesAll()
        }

        resetExchangeRatesAll() {
            this.exchangeRates.forEach(exchangeRate => {
                this.resetExchangeRate(exchangeRate)
            })
        }

        resetExchangeRate(rate) {
            rate.exchangeRate = (1 / this.exchangeRatesFromAPI[rate.currencyTo])?.toFixed(2)
        }

        addExchangeRate() {
            const newRate = {
                currencyFrom: this.currencyForPayment,
                currencyTo: this.clientPriceCurrencies[0],
                exchangeRate: null,
            }
            this.resetExchangeRate(newRate)
            this.exchangeRates.push(newRate)
        }

        removeRate(rate) {
            const rateIndex = this.exchangeRates.findIndex(el => el === rate)
            this.exchangeRates.splice(rateIndex, 1)
        }

        async pay() {
            this.isPaymentLoading = true
            const success = this.isOfflinePayment ? await this.makeOfflinePayment() : await this.makeOnlinePayment()
            this.isPaymentLoading = false
            if (success) {
                this.completePayment()
            }
        }

        async makeOfflinePayment() {
            try {
                const transactionDateTime =
                    this.transactionDateTime === this.$dateFns.format(new Date())
                        ? this.$dateFns.format(new Date(), "yyyy-MM-dd'T'HH:mm")
                        : `${this.transactionDateTime}T00:00`
                await this.$api.manualInvoicePaymentByKey.put({
                    invoicePaymentKey: this.paymentMethodObject.invoicePaymentKey,
                    remark: this.remark,
                    transactionDateTime,
                    ...(this.isShowBankTransferForm && {bankTransferDetails: this.bankTransferDetails}),
                    ...(this.exchangeRates?.length && {exchangeRates: this.exchangeRates}),
                })
                return true
            } catch (e) {
                this.$toast.error(this.$t('error_message.manual_invoice_update_error'))
                return false
            }
        }

        async makeOnlinePayment() {
            try {
                const paymentCompletePageQuery = status => {
                    return {
                        invoicePaymentKey: this.paymentMethodObject.invoicePaymentKey,
                        orderId: this.orderId,
                        status: status,
                        remark: this.remark,
                        transactionDateTime: this.transactionDateTime,
                    }
                }

                const getPaymentUrl = paymentStatus => {
                    return (
                        window.location.origin +
                        this.$router.resolve({
                            name: 'manualInvoicePaymentCompletePage',
                            query: paymentCompletePageQuery(paymentStatus),
                        }).href
                    )
                }

                const rq = {
                    invoicePaymentKey: this.paymentMethodObject.invoicePaymentKey,
                }
                const rqOnline = {
                    ...rq,
                    ...{
                        successUrl: getPaymentUrl('success'),
                        failureUrl: getPaymentUrl('failure'),
                        cancelUrl: getPaymentUrl('cancel'),
                    },
                }

                const paymentInitiateRs = await persistentStore.paymentInitiate(rqOnline)

                if (paymentInitiateRs.simpleRedirect) {
                    if (paymentInitiateRs.parameters.length) {
                        const query = new URLSearchParams(
                            paymentInitiateRs.parameters.map(({name, value}) => [name, value])
                        )
                        window.location = paymentInitiateRs.url + '?' + query
                    } else {
                        window.location = paymentInitiateRs.url
                    }
                } else {
                    const paymentMethod = this.paymentMethodObject
                    if (paymentMethod.paymentMethodCode === 'webpay' && this.$conf.public.webpayContractDomain) {
                        const {url, parameters, paymentNumber, paymentTransactionId} = paymentInitiateRs
                        await this.$refs.paymentRedirectForm.submit({
                            url: `${this.$conf.public.webpayContractDomain}/payment-redirect`,
                            parameters,
                            paymentNumber,
                            paymentTransactionId,
                            originUrl: url,
                        })
                    } else if (paymentMethod.paymentMethodCode === 'stripe') {
                        const {APIPublicKey, sessionId} = paymentInitiateRs.parameters.reduce(
                            (parameters, {name, value}) => {
                                parameters[name] = value
                                return parameters
                            },
                            {}
                        )
                        const {loadStripe} = await import('@stripe/stripe-js')
                        const stripe = await loadStripe(APIPublicKey)
                        await stripe.redirectToCheckout({sessionId})
                    } else if (paymentMethod.paymentMethodCode === 'adyen') {
                        const findParameter = (name, array) => {
                            return array.find(e => e.name === name)?.value
                        }
                        const AdyenCheckout = (await import('@adyen/adyen-web')).default
                        const checkout = await AdyenCheckout({
                            session: {
                                id: findParameter('SESSION_ID', persistentStore.paymentInitiateResponse.parameters),
                                sessionData: findParameter(
                                    'SESSION_DATA',
                                    persistentStore.paymentInitiateResponse.parameters
                                ),
                            },
                            clientKey: findParameter(
                                'CLIENT_API_KEY',
                                persistentStore.paymentInitiateResponse.parameters
                            ),
                            environment: findParameter(
                                'ENVIRONMENT',
                                persistentStore.paymentInitiateResponse.parameters
                            ),
                            onPaymentCompleted: res => {
                                if (res.resultCode.toLowerCase() === 'authorised') {
                                    const params = {
                                        paymentNumber: paymentInitiateRs.paymentNumber,
                                        ...res,
                                    }
                                    window.location = `${getPaymentUrl('success')}&${new URLSearchParams(
                                        params
                                    ).toString()}`
                                } else {
                                    window.location = getPaymentUrl('failure')
                                }
                            },
                            onError: () => {},
                        })

                        checkout.create('dropin').mount('#dropin-container')
                    } else if (paymentMethod.paymentMethodCode === 'nmi') {
                        const params = paymentInitiateRs.parameters.reduce((parameters, {name, value}) => {
                            parameters[name] = value
                            return parameters
                        }, {})

                        let my_awesome_script = document.createElement('script')

                        my_awesome_script.setAttribute(
                            'src',
                            'https://cibcfcib.transactiongateway.com/token/Collect.js'
                        )
                        my_awesome_script.setAttribute('data-tokenization-key', params['public-key'])
                        my_awesome_script.setAttribute('data-variant', 'inline')
                        my_awesome_script.onload = async () => {
                            const params = {
                                paymentNumber: paymentInitiateRs.paymentNumber,
                            }
                            this.nmiPaymentSuccessUrl = `${getPaymentUrl('success')}&${new URLSearchParams(
                                params
                            ).toString()}`
                        }
                        document.head.appendChild(my_awesome_script)
                    } else if (paymentMethod.paymentMethodCode === 'flywire') {
                        return new Promise(resolve => {
                            const params = paymentInitiateRs.parameters.reduce((parameters, {name, value}) => {
                                parameters[name] = value
                                return parameters
                            }, {})
                            const {callbackId, callbackUrl, callbackVersion, paymentFormUrl, recipientCode} = params
                            const paymentAuthorization = {
                                type: params['paymentAuthorization.type'],
                            }
                            const recipientFields = {
                                booking_reference: params['recipientFields.booking_reference'],
                            }

                            const config = {
                                env: params.env,
                                amount: paymentMethod.paymentAmount,
                                recipientCode,
                                recipientFields,
                                callbackId,
                                callbackUrl,
                                callbackVersion,
                                paymentFormUrl,
                                ...(paymentAuthorization?.type && {paymentAuthorization}),
                                requestPayerInfo: true,
                                onCompleteCallback: args => {
                                    const params = {
                                        paymentNumber: paymentInitiateRs.paymentNumber,
                                        reference: args.reference,
                                    }

                                    window.location = `${getPaymentUrl('success')}&${new URLSearchParams(
                                        params
                                    ).toString()}`
                                    resolve(true)
                                },
                                onCancel: () => {
                                    resolve(false)
                                },
                                onInvalidInput: errors => {},
                            }

                            let modal = window.FlywirePayment?.initiate(config)
                            modal?.render()
                        })
                    } else {
                        await this.$refs.paymentRedirectForm.submit(paymentInitiateRs)
                    }
                }
                return true
            } catch (e) {
                EventBus.$emit(SHOW_NOTIFICATIONS, {
                    notifications: [this.$t('error_message.payment_initiate_error')],
                    color: 'error',
                })
                return false
            }
        }

        onNMIPaymentFinish(response) {
            const params = {
                payment_token: response.token,
                first_name: response.firstName,
                last_name: response.lastName,
                address: response.address,
                email: response.email,
            }

            window.location = `${this.nmiPaymentSuccessUrl}&${new URLSearchParams(params).toString()}`
        }

        getNewExchangeAmount(rate) {
            return (rate.exchangeRate * this.amountForPayment)?.toFixed(2)
        }

        get isButtonDisabled() {
            return !this.paymentMethodObject || (this.isShowBankTransferForm && !this.valid)
        }

        get isShowBankTransferForm() {
            return (
                this.paymentMethodObject &&
                this.isOfflinePayment &&
                (!this.paymentMethodObject.paymentMethod ||
                    this.paymentMethodObject.paymentMethod.includes('Bank transfer'))
            )
        }

        get isOfflinePayment() {
            return !this.paymentMethodObject?.paymentMethodCode?.length
        }

        get currencyForPayment() {
            return this.paymentMethods[0]?.currency
        }

        get amountForPayment() {
            return this.paymentMethods[0]?.paymentAmount
        }

        get isTO1() {
            return authStore.isTO1
        }

        get canChangeExchangeRates() {
            return this.isTO1 && this.invoiceType === 'SUPPLIER'
        }

        get personRole() {
            return authStore.personRole
        }

        get invoiceBackdatingDays() {
            return (
                +runtimeStore.getCRMOption('asp.option.backdating.and.cancel.paid.manual.invoices.for.director.days') ||
                0
            )
        }

        get minPayDate() {
            if (
                this.$conf.public.account.paymentSchedulerPayBackdatingAndPaidInvoiceCancelForDirector &&
                this.isTO1 &&
                ['director', 'support'].includes(this.personRole)
            ) {
                return null
            } else if (
                this.$conf.public.account.paymentSchedulerPayBackdatingAndPaidInvoiceCancelForDirector &&
                ['supervisor'].includes(this.personRole) &&
                this.invoiceBackdatingDays > 0
            ) {
                return this.$dateFns.format(this.$dateFns.subDays(new Date(), this.invoiceBackdatingDays))
            }

            return this.$dateFns.format(new Date())
        }

        get isInitFlywire() {
            return !!this.paymentMethods?.find(paymentMethod => paymentMethod.paymentMethodCode === 'flywire')
        }

        @Watch('isInitFlywire')
        onIsInitFlywireChange() {
            if (this.isInitFlywire) {
                useHead({
                    script: [
                        {
                            src: 'https://checkout.flywire.com/flywire-payment.js',
                            async: true,
                            defer: true,
                        },
                    ],
                })
            }
        }
    }

    export default OrderManualInvoicePaymentForm
</script>
